import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Profile } from '../lib/auth';
import { homeForProfile } from '../lib/refonteNav';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  profile: Profile | null;
  error: string | null;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: false,
  profile: null,
  error: null,
  refreshAuth: async () => {},
  signOut: async () => {}
});

export const useAuth = () => useContext(AuthContext);

const publicAuthRoutes = [
  '/login',
  '/login/code',
  '/auth/confirm',
  '/auth/invite',
  '/auth/magic',
  '/auth/change-email',
  '/auth/reset',
  '/auth/reauthenticate'
];

// Un login OAuth (Google/MS) revient avec le token/code dans l'URL. Tant qu'il est là, il ne
// faut NI rediriger NI conclure « non connecté » : Supabase doit d'abord établir la session.
export const hasOAuthCallback = () =>
  typeof window !== 'undefined' && (
    window.location.hash.includes('access_token') ||
    window.location.hash.includes('error') ||
    new URLSearchParams(window.location.search).has('code')
  );

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check existing session on mount
  useEffect(() => {
    let isMounted = true;

    const checkExistingSession = async () => {
      console.log('🔄 Checking existing session on mount');
      const oauth = hasOAuthCallback();

      try {
        const { getCurrentUser } = await import('../lib/auth');
        const profile = await getCurrentUser();

        if (!isMounted) return;

        if (profile) {
          console.log('✅ Existing session found, user authenticated:', profile.role);
          setIsAuthenticated(true);
          setProfile(profile);
          setError(null);
          setIsLoading(false);
        } else if (oauth) {
          // Retour OAuth en cours : la session arrive via onAuthStateChange. On NE conclut PAS
          // « non connecté » (sinon redirection → token effacé → boucle). Filet : 6 s max.
          console.log('⏳ OAuth callback en cours, attente de la session…');
          setTimeout(() => { if (isMounted) setIsLoading(false); }, 6000);
        } else {
          console.log('ℹ️ No existing session found');
          setIsAuthenticated(false);
          setProfile(null);
          setIsLoading(false);
        }

      } catch (err: any) {
        if (!isMounted) return;
        console.error('❌ Session check error:', err);
        setError(null);
        if (oauth) {
          setTimeout(() => { if (isMounted) setIsLoading(false); }, 6000);
        } else {
          setIsAuthenticated(false);
          setProfile(null);
          setIsLoading(false);
        }
      }
    };

    checkExistingSession();

    return () => {
      isMounted = false;
    };
  }, []);

  // Écoute les changements de session (indispensable pour le SSO : au retour de Google/Microsoft,
  // la session s'établit de façon ASYNCHRONE — sans cet écouteur, l'app ne voit jamais la connexion
  // et reboucle sur /login). On recharge le profil à chaque SIGNED_IN / refresh, on le vide au logout.
  useEffect(() => {
    let active = true;
    let unsub: (() => void) | null = null;
    (async () => {
      const { supabase, getCurrentUser } = await import('../lib/auth');
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        // Ne jamais appeler d'autres fonctions supabase de façon synchrone ici (risque de deadlock).
        setTimeout(async () => {
          if (!active) return;
          if (event === 'SIGNED_OUT' || !session?.user) {
            setIsAuthenticated(false); setProfile(null); setIsLoading(false); return;
          }
          try {
            const p = await getCurrentUser();
            if (!active) return;
            setIsAuthenticated(!!p); setProfile(p); setError(null);
          } catch {
            if (active) { setIsAuthenticated(false); setProfile(null); }
          } finally {
            if (active) setIsLoading(false);
          }
        }, 0);
      });
      unsub = () => data.subscription.unsubscribe();
    })();
    return () => { active = false; if (unsub) unsub(); };
  }, []);

  // Manual refresh function (only called after explicit login)
  const refreshAuth = async () => {
    console.log('🔄 Manual auth refresh requested');

    try {
      const { getCurrentUser } = await import('../lib/auth');
      const profile = await getCurrentUser();

      if (profile) {
        console.log('✅ Profile loaded successfully:', profile.role);
        setIsAuthenticated(true);
        setProfile(profile);
        setError(null);
      } else {
        console.warn('⚠️ No profile found');
        setIsAuthenticated(false);
        setProfile(null);
      }

    } catch (err: any) {
      console.error('❌ Auth refresh error:', err);
      setError('Erreur lors du chargement du profil');
      setIsAuthenticated(false);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out function
  const handleSignOut = async () => {
    console.log('🔄 Sign out requested');

    try {
      const { signOut } = await import('../lib/auth');
      await signOut();

      setIsAuthenticated(false);
      setProfile(null);
      setError(null);

      console.log('✅ Sign out successful');

      navigate('/login', { replace: true });
    } catch (err: any) {
      console.error('❌ Sign out error:', err);
      setIsAuthenticated(false);
      setProfile(null);
      setError(null);
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };
  // Enhanced redirect logic - respect public auth routes
  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    // Callback OAuth en cours (Google/MS) : l'URL porte encore le `code`/`access_token` que
    // Supabase doit échanger. Surtout NE PAS rediriger maintenant, sinon on efface le code de
    // l'URL avant l'échange → la session ne s'établit jamais → boucle sur /login.
    const hasOAuthCallback =
      typeof window !== 'undefined' && (
        window.location.hash.includes('access_token') ||
        window.location.hash.includes('error') ||
        new URLSearchParams(window.location.search).has('code')
      );
    if (hasOAuthCallback) {
      return;
    }

    const isLoginRoute = location.pathname === '/login';
    const isPublicAuthRoute = publicAuthRoutes.includes(location.pathname);

    const performRedirect = () => {
      if (isAuthenticated && isLoginRoute) {
        console.log('🔄 Redirecting authenticated user from login to home');
        navigate(homeForProfile(profile), { replace: true });
      } else if (!isAuthenticated && !isPublicAuthRoute) {
        console.log('🔄 Redirecting unauthenticated user to login');
        navigate('/login', { replace: true });
      }
    };

    const timeoutId = setTimeout(performRedirect, 0);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, isLoading, location.pathname, navigate, profile?.role]);

  const contextValue = React.useMemo<AuthContextType>(() => ({
    isAuthenticated,
    isLoading,
    profile,
    error,
    refreshAuth,
    signOut: handleSignOut
  }), [isAuthenticated, isLoading, profile, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};